// @flow
// $FlowFixMe
import React, { Fragment, useEffect, useState, type Node } from "react";
import { get, uniqWith } from "lodash";
import type { UnaryFn } from "@tvg/types/Functional";
import type {
  RunnerFlags as RunnerFlagsType,
  RaceTypeCodeEnum
} from "@tvg/types/Race";
import { replaceCAPIVariables } from "@tvg/utils/capiUtils";
import RunnerNumber from "../../_atom/RunnerNumber";
import buildColor from "../../_static/ColorPalette";
import Icon from "../../_static/Icons";
import { question } from "../../_static/Icons/icons";
import Tag from "../../_atom/Tag";
import ModalV2 from "../../_templates/ModalV2";
import TagList from "../TagList";
import {
  RunnerFlagsWrapper,
  ContentWrapper,
  IconWrapper,
  RunnerNumberContainer
} from "./styled-components";

type FlagConfig = {
  id: string,
  name: string,
  description: string,
  isCondensed: boolean
};

export type HandicappingRunnerFlags = {
  title: string,
  subtitle: string,
  subtitleRunnerFlags: string,
  flags: Array<FlagConfig>
};

type Props = {
  runnerId: string,
  runnerName: string,
  raceTypeCode: RaceTypeCodeEnum,
  flags: RunnerFlagsType,
  maxFlags: ?number,
  handicappingRunnerFlags: HandicappingRunnerFlags,
  helpShown: boolean,
  hasBackNavigation: boolean
};

const mapToTag = (flag: FlagConfig) => ({
  name: flag.name,
  description: flag.description,
  tagType: "RunnerFlag",
  icon: null,
  iconSize: 16,
  uppercase: true,
  className: "runner-flag",
  qaLabel: `flag-${flag.id}`,
  isCondensed: true
});

const renderContentWrapper = (
  title: string | Node,
  flags: Array<FlagConfig>,
  hasSeparator = false
) => (
  <ContentWrapper hasSeparator={hasSeparator}>
    <TagList title={title} tags={flags.map((flag) => mapToTag(flag))} />
  </ContentWrapper>
);

const getRunnerTitle = (
  titleTemplate: string,
  runnerId: string,
  runnerName: string,
  raceTypeCode: RaceTypeCodeEnum
) => (
  <Fragment>
    {replaceCAPIVariables(titleTemplate, {
      runnerName: ""
    })}
    <RunnerNumberContainer>
      <RunnerNumber
        raceTypeCode={raceTypeCode}
        runnerId={runnerId}
        isFavorite={false}
      />
    </RunnerNumberContainer>
    {runnerName}
  </Fragment>
);

const renderFlagsHelpContent = (
  runnerId: string,
  runnerName: string,
  raceTypeCode: RaceTypeCodeEnum,
  handicappingRunnerFlags: HandicappingRunnerFlags,
  runnerFlags = []
) => {
  const flagsUniq = uniqWith(
    handicappingRunnerFlags.flags,
    (flagCfgA, flagCfgB) => flagCfgA.name === flagCfgB.name
  );

  // runner flags config
  const RunnerFlagsTitle = getRunnerTitle(
    handicappingRunnerFlags.subtitleRunnerFlags,
    runnerId,
    runnerName,
    raceTypeCode
  );
  const runnerFlagsConfig =
    runnerFlags && runnerFlags.length > 0
      ? flagsUniq.filter((handicappingFlag) =>
          runnerFlags.includes(handicappingFlag.id)
        )
      : [];
  // other flags config
  const hasSeparator = true;
  const otherFlagsTitle = handicappingRunnerFlags.subtitle;
  const otherFlagsConfig = flagsUniq.filter(
    (handicappingFlag) => !runnerFlags.includes(handicappingFlag.id)
  );

  return (
    <Fragment>
      {runnerFlagsConfig.length > 0 &&
        renderContentWrapper(RunnerFlagsTitle, runnerFlagsConfig)}
      {renderContentWrapper(otherFlagsTitle, otherFlagsConfig, hasSeparator)}
    </Fragment>
  );
};

const renderFlagsHelpModal = (
  runnerId: string,
  runnerName: string,
  raceTypeCode: RaceTypeCodeEnum,
  handicappingRunnerFlags: HandicappingRunnerFlags,
  flagsList: Array<string>,
  isModalOpen: boolean,
  setIsModalOpen: UnaryFn<boolean, void>,
  hasBackNavigation = false
) => {
  const modalProps = {
    isOpen: isModalOpen,
    isLastModal: true,
    title: handicappingRunnerFlags.title,
    titleType: "ipp",
    ...(hasBackNavigation
      ? {
          animation: "right",
          hasCloseButton: false,
          onBack: () => {
            setIsModalOpen(false);
          }
        }
      : {
          animation: "bottom",
          hasCloseButton: true,
          onClose: () => {
            setIsModalOpen(false);
          }
        })
  };

  return (
    <ModalV2 {...modalProps}>
      {() =>
        renderFlagsHelpContent(
          runnerId,
          runnerName,
          raceTypeCode,
          handicappingRunnerFlags,
          flagsList
        )
      }
    </ModalV2>
  );
};

const RunnerFlags = (props: Props) => {
  const {
    runnerId,
    runnerName,
    raceTypeCode,
    flags,
    maxFlags,
    handicappingRunnerFlags,
    helpShown,
    hasBackNavigation
  } = props;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [flagsList, setFlagsList] = useState([]);

  useEffect(() => {
    setFlagsList(
      Object.keys(flags).filter((flag) => flags[flag] && flag !== "__typename")
    );
  }, [runnerName, flags]);

  const getFlagConfig = (flag: string) =>
    get(handicappingRunnerFlags, "flags", []).find(
      (flagConfig) => flagConfig.id === flag
    ) || { id: flag, name: flag, description: flag, isCondensed: true };

  return (
    flagsList.length > 0 && (
      <Fragment>
        <RunnerFlagsWrapper>
          {flagsList.slice(0, maxFlags || flagsList.length).map((flag) => (
            <Tag isCondensed key={flag} {...mapToTag(getFlagConfig(flag))} />
          ))}
          {helpShown && !!handicappingRunnerFlags && (
            <IconWrapper onClick={() => setIsModalOpen(true)}>
              <Icon
                icon={question}
                color={buildColor("blue_accent", "500")}
                size={16}
              />
            </IconWrapper>
          )}
        </RunnerFlagsWrapper>

        {helpShown &&
          !!handicappingRunnerFlags &&
          renderFlagsHelpModal(
            runnerId,
            runnerName,
            raceTypeCode,
            handicappingRunnerFlags,
            flagsList,
            isModalOpen,
            setIsModalOpen,
            hasBackNavigation
          )}
      </Fragment>
    )
  );
};

RunnerFlags.defaultProps = {
  runnerId: "",
  runnerName: "",
  raceTypeCode: "T",
  flags: {
    horseInFocus: false,
    warningHorse: false,
    jockeyUplift: false,
    trainerUplift: false,
    horsesForCoursePos: false,
    horsesForCourseNeg: false,
    hotTrainer: false,
    coldTrainer: false,
    highestLastSpeedRating: false,
    sectionalFlag: false,
    significantImprover: false,
    jockeyInForm: false,
    clearTopRated: false,
    interestingJockeyBooking: false,
    firstTimeBlinkers: false
  },
  maxFlags: null,
  handicappingRunnerFlags: {
    title: "",
    subtitle: "",
    subtitleRunnerFlags: "",
    flags: []
  },
  helpShown: false,
  hasBackNavigation: false
};

export default RunnerFlags;
